import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@invent/wl-ui-kit-next/ui';
import { X } from 'lucide-react';
import * as React from 'react';

import { StatusChip } from '@/design';
import { formatDateTime } from '@/domains/survey';
import type {
  IMailPlaceholder,
  IMailTemplate,
  IMailVariant,
  IMailVersion,
  MailSurvey
} from '../../api/mail-template-contracts';
import { PreviewBody, PreviewToolbar, usePreview, type IPreviewOptions } from './mail-preview';
import {
  findPlaceholder,
  PILL_FALLBACK_CLASS,
  pillClass,
  pillTitle,
  pillWords,
  placeholderToken,
  placeholderWords
} from './placeholder-text';
import { richText } from './rich-part';
import {
  bodyOf,
  formatEt,
  isRich,
  lineText,
  sortProblems,
  surveyLabel,
  versionStamp
} from './template-fields';
import { wordDiff } from './word-diff';

/**
 * A variant's history (Google Docs style): a panel at the right listing its versions, newest first
 * and the base's default last — current, scheduled, past or withdrawn, each with its note; the
 * version picked there is shown in the page's main area — its preview, and what it changes against
 * the current version, word by word (a rich part by its text). Restoring puts it into the editor as
 * unsaved changes; Save keeps them as the draft, Publish makes them the next version.
 */

/** The page's width the panel takes from the main area at `lg`, so nothing hides under it. */
export const HISTORY_ROOM = 'lg:mr-[26.5rem]';

export function HistorySheet({
  open,
  onOpenChange,
  template,
  survey,
  variant,
  picked,
  onPick,
  onRestore
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: IMailTemplate;
  survey: Nullable<MailSurvey>;
  variant: Nullable<IMailVariant>;
  picked: Nullable<number>;
  onPick: (version: number) => void;
  onRestore: (version: IMailVersion) => void;
}) {
  const versions = variant?.versions ?? [];
  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        className="w-[min(26rem,calc(100vw-2rem))] gap-0 p-0"
        // The page stays usable beside the panel: a click there must not close it.
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
        data-mail-history=""
      >
        <SheetHeader className="px-5 pt-5 pb-4">
          <SheetTitle className="text-[0.9375rem] text-[var(--color-royal-blue)]">History</SheetTitle>
          <SheetDescription>
            {survey
              ? `${template.name} for ${surveyLabel(survey)}: its own versions, newest first.`
              : `Every published version of ${template.name}, newest first; the default last.`}
          </SheetDescription>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="ml-auto size-8"
            aria-label="Close history"
            onClick={() => onOpenChange(false)}
          >
            <X className="size-4" aria-hidden />
          </Button>
        </SheetHeader>
        {versions.length === 0 && (
          <p className="px-5 text-[0.8125rem] text-muted-foreground">
            No version of its own yet: {surveyLabel(survey)} mails use the text for all surveys.
          </p>
        )}
        <ul className="grid content-start gap-1 overflow-y-auto p-3 text-[0.8125rem]" aria-label="Versions">
          {versions.map((version) => {
            const current = version.status === 'current' || (version.version === 0 && variant?.current?.version === 0);
            const selected = version.version === picked;
            return (
              <li
                key={version.version}
                data-mail-version={version.version}
                className={cn(
                  'grid gap-2 rounded-md border',
                  selected
                    ? 'border-[var(--color-teal)] bg-[color-mix(in_srgb,var(--color-turquoise)_10%,white)]'
                    : 'border-transparent'
                )}
              >
                <button
                  type="button"
                  aria-current={selected ? 'true' : undefined}
                  onClick={() => onPick(version.version)}
                  className="grid gap-1 rounded-md px-3 py-2 text-left outline-none hover:bg-[color-mix(in_srgb,var(--color-turquoise)_6%,white)] focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="font-medium text-[var(--color-royal-blue)] tabular-nums">
                      {version.version === 0 ? 'Default' : `Version ${version.version}`}
                    </span>
                    <VersionChips version={version} current={current} />
                  </span>
                  {version.version !== 0 && (
                    <span className="text-xs text-muted-foreground">
                      {[version.createdAt && formatDateTime(version.createdAt), version.createdBy?.email]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  )}
                  {version.version !== 0 && <VersionWhen version={version} />}
                  {version.note && (
                    <span className="line-clamp-3 text-xs whitespace-pre-line" data-version-note="">
                      {version.note}
                    </span>
                  )}
                </button>
                {selected && !current && (
                  <div className="px-3 pb-3">
                    <Button type="button" size="sm" onClick={() => onRestore(version)}>
                      {version.version === 0 ? 'Restore default' : 'Restore this version'}
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </SheetContent>
    </Sheet>
  );
}

/** A version's state in the list and over its view. */
function VersionChips({ version, current }: { version: IMailVersion; current: boolean }) {
  return (
    <>
      {current && <StatusChip tone="success">Current</StatusChip>}
      {version.status === 'scheduled' && <StatusChip tone="info">Scheduled</StatusChip>}
      {version.status === 'withdrawn' && <StatusChip tone="neutral">Withdrawn</StatusChip>}
      {(version.problems ?? []).length > 0 && <StatusChip tone="attention">Fails the checks</StatusChip>}
    </>
  );
}

/** When a version took, takes or would have taken effect. */
function VersionWhen({ version }: { version: IMailVersion }) {
  const words =
    version.status === 'withdrawn'
      ? version.withdrawnAt && `Withdrawn ${formatDateTime(version.withdrawnAt)}`
      : version.effectiveAt &&
        version.effectiveAt !== version.createdAt &&
        `${version.status === 'scheduled' ? 'Takes effect' : 'In effect from'} ${formatEt(version.effectiveAt)}`;
  return words ? <span className="text-xs text-muted-foreground">{words}</span> : null;
}

/** The picked version in the main area: what it changes against the current one, and its mail. */
export function VersionView({
  template,
  survey,
  version,
  against,
  options,
  onOptions
}: {
  template: IMailTemplate;
  survey: Nullable<MailSurvey>;
  version: IMailVersion;
  /** The current version it is compared with. */
  against: Nullable<IMailVersion>;
  options: IPreviewOptions;
  onOptions: (options: IPreviewOptions) => void;
}) {
  const body = React.useMemo(() => bodyOf(template.parts, version), [template.parts, version]);
  const before = React.useMemo(() => bodyOf(template.parts, against), [template.parts, against]);
  const keys = React.useMemo(() => template.parts.map(({ key }) => key), [template.parts]);
  const preview = usePreview(template.code, survey, body, keys, options);
  const current = against !== null && version.version === against.version && version.survey === against.survey;
  const problems = sortProblems(version.problems ?? [], keys);
  const blockLabel = (id: string) => template.systemBlocks.find((block) => block.id === id)?.label ?? id;
  const rows = [
    ...template.parts.map(({ key, label }) => {
      const words = (side: typeof body) => {
        const part = side.parts[key];
        return isRich(part) ? richText(part.doc, blockLabel) : lineText(part);
      };
      return { key, label, before: words(before), after: words(body) };
    }),
    { key: 'fromName', label: 'Sender name', before: before.settings.fromName, after: body.settings.fromName },
    { key: 'replyTo', label: 'Reply-To', before: before.settings.replyTo, after: body.settings.replyTo },
    { key: 'design', label: 'Design', before: designWords(before.settings.design), after: designWords(body.settings.design) }
  ];
  const changed = rows.filter((row) => row.before !== row.after);
  const same = rows.filter((row) => row.before === row.after);

  return (
    <div className="grid min-w-0 gap-4" data-version-view={version.version}>
      <section
        aria-label="Changes"
        className="grid gap-3 rounded-md border border-border bg-white p-4 text-[0.8125rem]"
      >
        <h2 className="flex flex-wrap items-center gap-2 text-base font-semibold text-[var(--color-royal-blue)]">
          {versionStamp(version.version, version.createdAt, version.createdBy)}
          <VersionChips version={version} current={current} />
        </h2>
        <VersionWhen version={version} />
        {version.note && (
          <p className="whitespace-pre-line" data-version-note="">
            {version.note}
          </p>
        )}
        {(version.problems ?? []).length > 0 && (
          <ul className="grid gap-0.5 text-destructive" aria-label="Fails the checks">
            {rows
              .filter(({ key }) => problems.byField[key])
              .map(({ key, label }) => (
                <li key={key}>
                  {label}: {problems.byField[key]}
                </li>
              ))}
            {problems.other && <li>{problems.other}</li>}
          </ul>
        )}
        {current ? (
          <p className="text-muted-foreground">The current version.</p>
        ) : changed.length === 0 ? (
          <p className="text-muted-foreground">Same text as the current version.</p>
        ) : (
          <>
            <dl className="grid gap-3">
              {changed.map(({ key, label, before: was, after: is }) => (
                <div key={key} className="grid gap-1" data-diff-field={key}>
                  <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
                  <dd className="leading-relaxed whitespace-pre-wrap">
                    <FieldDiff before={was} after={is} placeholders={template.placeholders} />
                  </dd>
                </div>
              ))}
            </dl>
            {same.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Unchanged: {same.map(({ label }) => label).join(', ')}
              </p>
            )}
          </>
        )}
      </section>
      <section aria-label="Preview" className="grid gap-3 rounded-md border border-border bg-white p-4">
        <PreviewToolbar template={template} options={options} onChange={onOptions} />
        <PreviewBody
          state={preview}
          options={options}
          sender={body.settings.fromName}
          blocked="No preview: this version fails the checks."
        />
      </section>
    </div>
  );
}

/** The current version's text → the picked one's: added words green, removed ones struck red. */
function FieldDiff({
  before,
  after,
  placeholders
}: {
  before: string;
  after: string;
  placeholders: readonly IMailPlaceholder[];
}) {
  const parts = React.useMemo(() => wordDiff(before, after), [before, after]);
  return (
    <>
      {parts.map((part, index) => {
        const text = <DiffText text={part.text} placeholders={placeholders} />;
        if (part.kind === 'added') {
          return (
            <ins
              key={index}
              className="rounded-sm bg-[color-mix(in_srgb,var(--color-teal)_14%,white)] text-[#0b5e3a] underline decoration-[#0b5e3a]/60 underline-offset-2"
            >
              {text}
            </ins>
          );
        }
        if (part.kind === 'removed') {
          return (
            <del
              key={index}
              className="rounded-sm bg-[color-mix(in_srgb,var(--color-magenta)_10%,white)] text-[var(--color-deep-magenta)] line-through"
            >
              {text}
            </del>
          );
        }
        return <span key={index}>{text}</span>;
      })}
    </>
  );
}

/** A run of stored text with its placeholders drawn as pills. */
function DiffText({ text, placeholders }: { text: string; placeholders: readonly IMailPlaceholder[] }) {
  const pieces = text.split(/(\{\{[^{}]*\}\})/);
  return (
    <>
      {pieces.map((piece, index) => {
        const token = placeholderToken(piece);
        if (token === null) return piece;
        const known = findPlaceholder(placeholders, token.id);
        const words = pillWords(Boolean(known), token.id, token.fallback, known ? placeholderWords(known) : undefined);
        return (
          <span key={index} className={pillClass(Boolean(known))} title={pillTitle(known, token.id, token.fallback)}>
            {words.label}
            {words.fallback && <span className={PILL_FALLBACK_CLASS}> · {words.fallback}</span>}
          </span>
        );
      })}
    </>
  );
}

/** "Veritant", "Plain" — a design's code as words. */
function designWords(code: string): string {
  return code ? code.charAt(0).toUpperCase() + code.slice(1) : '';
}
